<template>
  <form @submit.prevent>
    <div>
      <h2>
        <span v-if="isEditMode">"{{ editedOrganization.name }}" {{ $t("common.update") }}</span>
        <span v-else>{{ $t("organization.new") }}</span>
      </h2>
      <form class="needs-validation" @submit.prevent="save">
        <div role="tablist">
          <b-card no-body class="mb-1 shadow-none">
            <b-card-header header-tag="header" role="tab">
              <a v-b-toggle="'accordion_organization'" class="text-dark" href="javascript: void(0);">
                <div class="row">
                  <div class="col-md-6">
                    <h5>
                      <i class="bx bx-sitemap"></i>
                      {{ $t("organization.information") }}
                    </h5>
                  </div>
                  <div class="col-md-6 text-right">
                    <i v-if="submitted && !$v.form.$anyError" class="bx bx-check text-success h1"></i>
                    <i v-if="submitted && $v.form.$anyError" class="bx bx-x text-danger h1"></i>
                  </div>
                </div>
              </a>
            </b-card-header>
            <b-collapse id="accordion_organization" :visible="true" accordion="accordion" role="tabpanel">
              <b-card-body>
                <ImageCropper id="imageCropper" :src="form.logo" @cropperImage="cropperImage" />

                <div class="row">
                  <div
                    class="
                      col-md-3
                      d-flex
                      flex-column
                      justify-content-center
                      align-items-center
                    "
                  >
                    <a href="javascript: void(0);" @click="openCropper">
                      <img
                        class="
                          avatar-title
                          rounded-circle
                          bg-light
                          text-danger
                          font-size-16
                        "
                        :src="form.logo"
                        alt
                      />
                    </a>
                    <a class="m-1 text-info" href="javascript: void(0);" @click="openCropper"><i class="bx bx-edit-alt text-info"></i> {{ $t("common.update") }}</a>
                  </div>
                  <div class="col-md-9">
                    <div class="form-group">
                      <div class="mb-3"></div>

                      <label>{{ $t("common.owner") }}</label>
                      <filter-select
                        :selected="form.createdBy"
                        id="organizationOwnerFilter"
                        ref="organizationOwnerFilter"
                        :placeholder="$t('common.owner')"
                        :multiple="false"
                        @filter="onAccountSearch"
                        @changed="onAccountSelect"
                      />

                      <label class="mt-2">{{ $t("organization.name") }}</label>
                      <input
                        v-model="form.name"
                        class="form-control"
                        :placeholder="$t('organization.enter-name')"
                        :class="{
                          'is-invalid': submitted && $v.form.name.$error,
                          'is-valid': submitted && !$v.form.name.$error,
                        }"
                      />

                      <label class="mt-2">{{ $t("country.name") }}</label>
                      <filter-select
                        id="countriesFilter"
                        ref="countriesFilter"
                        :placeholder="$t('country.select')"
                        :multiple="false"
                        @filter="onCountriesSearch"
                        :selected="form.country"
                        @changed="onCountriesSelect"
                        track="code"
                      />
                    </div>
                  </div>
                </div>
                <div class="row"></div>
              </b-card-body>
            </b-collapse>
          </b-card>

          <b-card no-body class="mb-1 shadow-none">
            <b-card-header header-tag="header" role="tab">
              <a v-b-toggle="'accordion_company'" class="text-dark" href="javascript: void(0);">
                <div class="row">
                  <div class="col-md-6">
                    <h5>
                      <i class="bx bx-building-house"></i>
                      {{ $t("organization.company-details") }}
                    </h5>
                  </div>
                  <div class="col-md-6 text-right">
                    <i v-if="submitted && !$v.form.$anyError" class="bx bx-check text-success h1"></i>
                    <i v-if="submitted && $v.form.$anyError" class="bx bx-x text-danger h1"></i>
                  </div>
                </div>
              </a>
            </b-card-header>
            <b-collapse id="accordion_company" :visible="false" accordion="accordion" role="tabpanel">
              <b-card-body>
                <div class="alert alert-danger" v-if="$v.form.$invalid">
                  <span v-if="!$v.form.nid.minLength"> {{ $t("organization.validation.nid-min-length", { length: $v.form.nid.$params.minLength.min }) }} </span>
                  <span v-if="!$v.form.nid.maxLength">{{ $t("organization.validation.nid-max-length", { length: $v.form.nid.$params.maxLength.max }) }} </span>
                  <span v-if="!$v.form.phone.minLength"> {{ $t("organization.validation.phone-min-length", { length: $v.form.phone.$params.minLength.min }) }} </span>
                  <span v-if="!$v.form.phone.maxLength">{{ $t("organization.validation.phone-max-length", { length: $v.form.phone.$params.maxLength.max }) }} </span>
                  <span v-if="!$v.form.phone.isPhone">{{ $t("organization.validation.phone-is-phone") }} </span>
                  <span v-if="$v.form.email.$invalid">{{ $t("organization.validation.email") }} </span>
                </div>

                <div class="form-row">
                  <div class="form-group col-md-6">
                    <sweet-input
                      class="mt-1"
                      v-model="form.nid"
                      :isValid="!$v.form.nid.$error"
                      @input="$v.form.nid.$touch()"
                      layout="vertical"
                      :placeholder="$t('organization.organization-nid')"
                      :label="$t('organization.organization-nid')"
                    />
                  </div>
                  <div class="form-group col-md-6">
                    <sweet-input
                      v-model="form.title"
                      :isValid="!$v.form.title.$error"
                      @input="$v.form.title.$touch()"
                      layout="vertical"
                      :placeholder="$t('organization.organization-title')"
                      :label="$t('organization.organization-title')"
                    />
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <sweet-input
                      v-model="form.taxOffice"
                      :isValid="!$v.form.taxOffice.$error"
                      @input="$v.form.taxOffice.$touch()"
                      layout="vertical"
                      :placeholder="$t('organization.tax-office')"
                      :label="$t('organization.tax-office')"
                    />
                  </div>
                  <div class="form-group col-md-6">
                    <sweet-input
                      v-model="form.web"
                      :isValid="!$v.form.web.$error"
                      @input="$v.form.web.$touch()"
                      layout="vertical"
                      :placeholder="$t('organization.web')"
                      :label="$t('organization.web')"
                    />
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label>{{ $t("organization.phone") }}</label>
                    <sweet-input
                      class="mt-1"
                      v-model="form.phone"
                      :isValid="!$v.form.phone.$error"
                      @input="$v.form.phone.$touch()"
                      layout="vertical"
                      :placeholder="$t('organization.phone')"
                      :label="$t('organization.phone')"
                    />
                  </div>
                  <div class="form-group col-md-6">
                    <sweet-input
                      v-model="form.email"
                      :isValid="!$v.form.email.$error"
                      @input="$v.form.email.$touch()"
                      layout="vertical"
                      :placeholder="$t('organization.email')"
                      :label="$t('organization.email')"
                    />
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <sweet-input
                      v-model="form.address"
                      :isValid="!$v.form.address.$error"
                      @input="$v.form.address.$touch()"
                      layout="vertical"
                      :placeholder="$t('organization.address')"
                      :label="$t('organization.address')"
                    />
                  </div>
                  <div class="form-group col-md-6">
                    <sweet-input
                      v-model="form.kep"
                      :isValid="!$v.form.kep.$error"
                      @input="$v.form.kep.$touch()"
                      layout="vertical"
                      :placeholder="$t('organization.kep')"
                      :label="$t('organization.kep')"
                    />
                  </div>
                </div>

                <!---->
              </b-card-body>
            </b-collapse>
          </b-card>

          <!-- <b-card no-body class="mb-1 shadow-none">
            <b-card-header header-tag="header" role="tab">
              <a v-b-toggle="'accordion_contact'" class="text-dark" href="javascript: void(0);">
                <div class="row">
                  <div class="col-md-6">
                    <h5>
                      <i class="bx bx-building-house"></i>
                      {{ $t("organization.contact-information") }}
                    </h5>
                  </div>
                  <div class="col-md-6 text-right">
                    <i v-if="submitted && !$v.form.$anyError" class="bx bx-check text-success h1"></i>
                    <i v-if="submitted && $v.form.$anyError" class="bx bx-x text-danger h1"></i>
                  </div>
                </div>
              </a>
            </b-card-header>
            <b-collapse id="accordion_contact" :visible="false" accordion="accordion" role="tabpanel">
              <b-card-body>
                <div class="alert alert-danger" v-if="$v.form.$invalid">
                  <span v-if="$v.form.contactEmail.$invalid">{{ $t("organization.validation.email") }} </span>
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <sweet-input
                      v-model="form.contactName"
                      :isValid="!$v.form.contactName.$error"
                      @input="$v.form.contactName.$touch()"
                      layout="vertical"
                      :placeholder="$t('organization.contact-name')"
                      :label="$t('organization.contact-name')"
                    />
                  </div>
                  <div class="form-group col-md-6">
                    <sweet-input
                      v-model="form.contactEmail"
                      :isValid="!$v.form.contactEmail.$error"
                      @input="$v.form.contactEmail.$touch()"
                      layout="vertical"
                      :placeholder="$t('organization.contact-email')"
                      :label="$t('organization.contact-email')"
                    />
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <sweet-input
                      v-model="form.contactPhone"
                      :isValid="!$v.form.contactPhone.$error"
                      @input="$v.form.contactPhone.$touch()"
                      layout="vertical"
                      :placeholder="$t('organization.contact-phone')"
                      :label="$t('organization.contact-phone')"
                    />
                  </div>
                  <div class="form-group col-md-6">
                    <sweet-input
                      v-model="form.contactAddress"
                      :isValid="!$v.form.contactAddress.$error"
                      @input="$v.form.contactAddress.$touch()"
                      layout="vertical"
                      :placeholder="$t('organization.contact-adress')"
                      :label="$t('organization.contact-adress')"
                    />
                  </div>
                </div>
              </b-card-body>
            </b-collapse>
          </b-card> -->

          <!-- <b-card no-body class="mb-1 shadow-none">
            <b-card-header header-tag="header" role="tab">
              <a v-b-toggle="'accordion_data_protection'" class="text-dark" href="javascript: void(0);">
                <div class="row">
                  <div class="col-md-6">
                    <h5>
                      <i class="bx bx-building-house"></i>
                      {{ $t("organization.data-protection-information") }}
                    </h5>
                  </div>
                  <div class="col-md-6 text-right">
                    <i v-if="submitted && !$v.form.$anyError" class="bx bx-check text-success h1"></i>
                    <i v-if="submitted && $v.form.$anyError" class="bx bx-x text-danger h1"></i>
                  </div>
                </div>
              </a>
            </b-card-header>
            <b-collapse id="accordion_data_protection" :visible="false" accordion="accordion" role="tabpanel">
              <b-card-body>
                <div class="alert alert-danger" v-if="$v.form.$invalid">
                  <span v-if="$v.form.dataProtectionEmail.$invalid">{{ $t("organization.validation.email") }} </span>
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <sweet-input
                      v-model="form.dataProtectionName"
                      :isValid="!$v.form.dataProtectionName.$error"
                      @input="$v.form.dataProtectionName.$touch()"
                      layout="vertical"
                      :placeholder="$t('organization.data-protection-name')"
                      :label="$t('organization.data-protection-name')"
                    />
                  </div>
                  <div class="form-group col-md-6">
                    <sweet-input
                      v-model="form.dataProtectionEmail"
                      :isValid="!$v.form.dataProtectionEmail.$error"
                      @input="$v.form.dataProtectionEmail.$touch()"
                      layout="vertical"
                      :placeholder="$t('organization.data-protection-email')"
                      :label="$t('organization.data-protection-email')"
                    />
                  </div>
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <sweet-input
                      v-model="form.dataProtectionPhone"
                      :isValid="!$v.form.dataProtectionPhone.$error"
                      @input="$v.form.dataProtectionPhone.$touch()"
                      layout="vertical"
                      :placeholder="$t('organization.data-protection-phone')"
                      :label="$t('organization.data-protection-phone')"
                    />
                  </div>
                  <div class="form-group col-md-6">
                    <sweet-input
                      v-model="form.dataProtectionAdress"
                      :isValid="!$v.form.dataProtectionAdress.$error"
                      @input="$v.form.dataProtectionAdress.$touch()"
                      layout="vertical"
                      :placeholder="$t('organization.data-protection-adress')"
                      :label="$t('organization.data-protection-adress')"
                    />
                  </div>
                </div>
              </b-card-body>
            </b-collapse>
          </b-card> -->
        </div>
      </form>
      <sweet-button @click="save()" type="submit" :loading="isUpdating" :disabled="isUpdating || $v.$invalid" variant="success" icon="mdi mdi-check" size="sm" buttonClass="mt-2">
        {{ $t("common.save") }}
      </sweet-button>
    </div>
  </form>
</template>

<script>
import ImageCropper from "@c/form/image-cropper";
import { email, maxLength, minLength, required } from "vuelidate/lib/validators";
import Countries from "../countries/countries.json";
import { image as defaultImage } from "./default-organization-image";

const state = "workspaceOrganization";
const schema = {
  logo: defaultImage,
  nid: undefined,
  title: undefined,
  workspace: undefined,
  taxOffice: undefined,
  phone: undefined,
  address: undefined,
  web: undefined,
  email: undefined,
  kep: undefined,
  createdBy: undefined,
  contactName: undefined,
  contactEmail: undefined,
  contactPhone: undefined,
  contactAddress: undefined,
  dataProtectionName: undefined,
  dataProtectionEmail: undefined,
  dataProtectionPhone: undefined,
  dataProtectionAdress: undefined,
};

export default {
  components: { ImageCropper },
  props: ["closeOnSave"],
  data() {
    return {
      request: {
        options: { page: 1, limit: 10 },
        query: {},
      },
      form: { ...schema, logo: defaultImage, country: [] },

      submitted: false,
    };
  },
  validations: {
    form: {
      country: {
        required,
      },
      createdBy: {
        required,
      },
      name: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(350),
      },
      workspace: {
        required,
      },
      nid: {
        minLength: minLength(10),
        maxLength: maxLength(11),
        // minLength: minLength(10), Eski sistemde Veri sorumlusu için kullanılan validasyon VKN/TCKN
        // maxLength: maxLength(11),
      },
      title: {},
      taxOffice: {},
      phone: {
        minLength: minLength(10),
        maxLength: maxLength(17),
        isPhone(value) {
          const regex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;
          return value ? regex.test(value) : true;
        },
      },

      email: { email },
      kep: {},
      address: {},
      web: {},
      contactName: {},
      contactEmail: { email },
      contactPhone: {},
      contactAddress: {},
      dataProtectionName: {},
      dataProtectionEmail: { email },
      dataProtectionPhone: {},
      dataProtectionAdress: {},
    },
  },
  computed: {
    isUpdating() {
      return this.$store.getters[`${state}/isUpdating`];
    },
    editedOrganization() {
      return this.$store.getters[`${state}/editedOrganization`];
    },
    isEditMode() {
      return this.$store.getters[`${state}/isEditMode`];
    },
  },
  watch: {
    editedOrganization: {
      deep: true,
      immediate: true,
      handler(v) {
        if (v) {
          this.form = { ...v, previousOwner: { _id: v.createdBy._id } };
        } else {
          this.resetForm();
        }
        if (!this.form.logo) {
          this.form.logo = defaultImage;
        }
      },
    },
    "form.workspace": {
      deep: true,
      immediate: true,
      handler(v, o) {
        if (this.form.workspace && v?._id != o?._id) {
          const account = this.form.createdBy;
          this.$refs.organizationOwnerFilter.reset();
          this.form.createdBy = account;
        }
      },
    },
    "form.createdBy": {
      deep: true,
      immediate: true,
      handler(v, o) {
        if (this.form._id && o) {
          this.form.previousOwner = { _id: o._id };
        }
      },
    },

    request: {
      deep: true,
      immediate: true,
      handler() {
        this.debounceMixin(() => this.paginate());
      },
    },
  },

  methods: {
    async save() {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }

      try {
        const workspace = this.form.workspace;
        delete this.form.workspace;
        await this.$store.dispatch(`${state}/save`, {
          organization: this.form,
          workspace,
        });

        this.resetForm();
        if (this.closeOnSave) {
          this.$root.$emit("bv::toggle::collapse", "sidebar-edit-account");
        }
        this.$toast.success(this.$t("common.success"));
        this.$root.$emit("bv::toggle::collapse", "sidebar-edit-organization");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    setWorkspace(workspace) {
      this.form = { ...this.form, workspace };
    },

    async onAccountSearch(value, callback) {
      if (!this.form.workspace) {
        callback([]);
        return;
      }
      const pagination = { options: { limit: 200 }, query: {} };

      const response = await this.$store.dispatch(`workspace/paginateMember`, {
        pagination,
        id: this.form.workspace?._id,
      });
      const filtered = response.data.docs.filter((r) => r.name.toLowerCase().includes(value.toLowerCase()));

      callback(filtered);
    },
    onAccountSelect(member) {
      this.form.createdBy = member?.account || member;
    },

    openCropper() {
      this.$bvModal.show("imageCropper");
    },
    cropperImage(value) {
      this.form.logo = value;
      this.$bvModal.hide("imageCropper");
      this.$forceUpdate();
    },
    resetForm() {
      this.form = { ...schema };
    },
    async onCountriesSearch(value, callback) {
      const lang = "tr";
      const response = Countries.filter((x) => x.name[lang].toLowerCase().includes(value.toLowerCase()));
      callback(response);
    },
    onCountriesSelect(item) {
      this.form = { ...this.form, country: item };
    },

    paginate() {
      this.$store.dispatch(`managementWorkspace/paginate`, this.request);
    },
  },
};
</script>
